import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Send, CalendarDays, BadgeCheck, HardHat, FileText, X, Mail, Download, AlertTriangle } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { StatusTrackingTable, StatusTone } from '../ui/StatusTrackingTable';
import { StatusFilterChips } from '../ui/StatusFilterChips';
import { SuiviExtrasParPoste, PosteSuivi } from './SuiviExtrasParPoste';
import { EtatPoste, LIBELLE_ETAT, etatDuPoste, etatLeMoinsAvance } from './extrasEtatPoste';
import { sameEmailBatch, batchActionLabel } from './extrasEmailBatch';
import { GroupedRequest, groupByPartnerAndEvent, postesIncomplets } from './extrasGrouping';
import { EmailHistoryModal } from '../ui/EmailHistoryModal';
import { useAuth } from '../../context/AuthContext';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { generateExtrasConfirmedPdf } from '../../lib/extrasConfirmedPdfGenerator';

interface RequestEntry {
    request_id: string;
    partner_id: string;
    partner_name: string;
    extra_type_id: string;
    type_label: string;
    desired_people: string[];
    confirmed_comment: string;
    event_id: string | null;
    event_label: string;
    quantity: number;
    request_date: string;
    start_time: string;
    end_time: string;
    status: 'a_demander' | 'demandee';
    requested_at: string | null;
    confirm_token: string;
    confirmed_at: string | null;
    document_file_name: string;
    document_mime_type: string;
    email_count: number;
    created_at: string;
    /** Renseigné quand l'horaire a changé APRÈS la confirmation du partenaire : start_time/end_time portent
     *  déjà le nouvel horaire, mais le partenaire a confirmé l'ancien et doit être reprévenu. */
    hours_changed_at?: string | null;
    /** Besoin total enregistre sur la privatisation pour ce poste, et ce qui est reellement pourvu
     *  (demandes partenaire + salaries internes bookes) - alimente le repere "2 sur 4 demandes". */
    need_total?: number;
    covered_total?: number;
    /** Compteurs SEPARES : interne et prestataire ne se confondent plus. Le process passe d'abord par les
     *  salaries de la maison, seul le reliquat part chez le prestataire. `sent_total` distingue ce qui est
     *  reellement parti de ce qui est seulement cree. */
    internal_total?: number;
    requested_total?: number;
    confirmed_total?: number;
    sent_total?: number;
    internal_possible?: boolean;
}

type StatusValue = 'a_demander' | 'demandee' | 'confirmee';

const STATUS_TONE: Record<StatusValue, StatusTone> = {
    a_demander: { label: 'À demander', className: 'bg-surface-soft text-content-muted' },
    demandee: { label: 'Demandée', className: 'bg-green-500/10 text-green-500' },
    confirmee: { label: 'Confirmée', className: 'bg-brand-500/10 text-brand-600 dark:text-brand-400' },
};

// Du moins avancé au plus avancé : le statut d'un groupe est celui de sa ligne la moins avancée,
// c'est ce qu'il reste à faire qui doit sauter aux yeux.
const STATUS_ORDER: StatusValue[] = ['a_demander', 'demandee', 'confirmee'];
const deriveStatus = (r: RequestEntry): StatusValue => (r.confirmed_at ? 'confirmee' : r.status === 'demandee' ? 'demandee' : 'a_demander');

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};
const fmtDateTime = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
};

const PartnerBadge: React.FC<{ name: string; id: string }> = ({ name, id }) => {
    const navigate = useNavigate();
    if (!name) return null;
    return (
        <button onClick={(e) => { e.stopPropagation(); navigate(`/extras-partenaires/${id}`); }}
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest bg-surface-soft border border-border text-content-muted hover:text-content hover:border-brand-500/40 transition-colors">
            <HardHat size={11} /> {name}
        </button>
    );
};

const RequestDetailModal: React.FC<{ entry: RequestEntry; actions?: React.ReactNode; onClose: () => void }> = ({ entry, actions, onClose }) => {
    const [docUrl, setDocUrl] = React.useState<string | null>(null);
    const isImage = (entry.document_mime_type || '').startsWith('image/');

    React.useEffect(() => {
        if (!entry.document_file_name) return;
        const token = localStorage.getItem('token');
        fetch(`/api/extras-requests/${entry.request_id}/document`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => (res.ok ? res.blob() : Promise.reject()))
            .then((blob) => setDocUrl(URL.createObjectURL(blob)))
            .catch(() => setDocUrl(null));
    }, [entry.request_id, entry.document_file_name]);

    return (
        <Modal open onClose={onClose} title={entry.type_label} icon={<HardHat size={16} />} maxWidth="max-w-md"
            footer={<>{actions}<Button variant="secondary" onClick={onClose}>Fermer</Button></>}>
            <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                    <span className="text-content-muted">Type</span>
                    <span className="text-content font-bold">{entry.type_label}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-content-muted">Partenaire</span>
                    <span className="text-content font-bold">{entry.partner_name}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-content-muted">Quantité</span>
                    <span className="text-content font-bold">{entry.quantity}</span>
                </div>
                {entry.desired_people?.length > 0 && (
                    <div className="flex items-start justify-between gap-2">
                        <span className="text-content-muted shrink-0">Personnes recommandées</span>
                        <span className="text-content font-bold text-right">{entry.desired_people.join(', ')}</span>
                    </div>
                )}
                {entry.event_label && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted">Privatisation</span>
                        <span className="text-content font-bold">{entry.event_label}</span>
                    </div>
                )}
                {entry.request_date && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted flex items-center gap-1.5"><CalendarDays size={13} /> Date</span>
                        <span className="text-content">{fmtDate(entry.request_date)} {entry.start_time ? `à ${entry.start_time}` : ''}{entry.end_time ? ` - ${entry.end_time}` : ''}</span>
                    </div>
                )}
                {/* Même alerte que sur la fiche partenaire : l'horaire a été corrigé après la confirmation,
                    le partenaire garde en tête l'ancien et doit être reprévenu. Emeni (2026-08-24). */}
                {entry.hours_changed_at && (
                    <div className="flex items-start gap-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                        <span>
                            Horaire modifié après la confirmation du partenaire. L'horaire ci-dessus est le bon,
                            mais il a confirmé l'ancien : renvoyez-lui la demande pour qu'il reconfirme.
                        </span>
                    </div>
                )}
                {entry.requested_at && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted flex items-center gap-1.5"><Send size={13} /> Demandée le</span>
                        <span className="text-content">{fmtDateTime(entry.requested_at)}</span>
                    </div>
                )}
                {entry.confirmed_at && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted flex items-center gap-1.5"><BadgeCheck size={13} /> Confirmée le</span>
                        <span className="text-content">{fmtDateTime(entry.confirmed_at)}</span>
                    </div>
                )}
                {entry.confirmed_comment && (
                    <div className="space-y-1">
                        <span className="text-content-muted block">Commentaire du prestataire</span>
                        <p className="text-content bg-surface-soft border border-border rounded-lg px-3 py-2 whitespace-pre-wrap">{entry.confirmed_comment}</p>
                    </div>
                )}
                {entry.document_file_name && (
                    <div className="space-y-1.5">
                        <span className="text-content-muted flex items-center gap-1.5"><FileText size={13} /> Document joint</span>
                        {isImage && docUrl && (
                            <a href={docUrl} target="_blank" rel="noreferrer">
                                <img src={docUrl} alt={entry.document_file_name} className="max-h-40 rounded-lg border border-border object-contain" />
                            </a>
                        )}
                        {docUrl ? (
                            <a href={docUrl} download={entry.document_file_name} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                <FileText size={13} /> {entry.document_file_name} — Télécharger
                            </a>
                        ) : (
                            <span className="text-content truncate block max-w-[280px]">{entry.document_file_name}</span>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export const ExtrasModule: React.FC = () => {
    const { user } = useAuth();
    const [aDemander, setADemander] = React.useState<RequestEntry[]>([]);
    const [demandees, setDemandees] = React.useState<RequestEntry[]>([]);
    const [confirmeesParPrestataire, setConfirmeesParPrestataire] = React.useState<RequestEntry[]>([]);
    const [besoinsSansDemande, setBesoinsSansDemande] = React.useState<RequestEntry[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [sendingId, setSendingId] = React.useState<string | null>(null);
    const [confirmingId, setConfirmingId] = React.useState<string | null>(null);
    const [cancelingId, setCancelingId] = React.useState<string | null>(null);
    const [error, setError] = React.useState('');
    const [selectedEntry, setSelectedEntry] = React.useState<RequestEntry | null>(null);
    const [emailHistoryFor, setEmailHistoryFor] = React.useState<RequestEntry | null>(null);
    const [statusFilter, setStatusFilter] = React.useState<'all' | StatusValue>('all');
    const [etatFilter, setEtatFilter] = React.useState<'all' | EtatPoste>('all');

    const load = React.useCallback((query?: string) => {
        setLoading(true);
        moduleApi.getAllExtrasRequests(query).then((d: any) => {
            setADemander(Array.isArray(d?.a_demander) ? d.a_demander : []);
            setDemandees(Array.isArray(d?.demandees) ? d.demandees : []);
            setConfirmeesParPrestataire(Array.isArray(d?.confirmees_par_prestataire) ? d.confirmees_par_prestataire : []);
            // Postes requis dont personne ne s'est encore occupe : ils n'ont aucune demande, donc ils
            // n'apparaissaient nulle part — alors que ce sont eux qui reclament une action.
            setBesoinsSansDemande(Array.isArray(d?.besoins_sans_demande) ? d.besoins_sans_demande : []);
        }).catch((e: any) => setError(e?.message || 'Erreur chargement')).finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        load(q.trim() || undefined);
    };

    const handleSend = async (r: RequestEntry) => {
        setSendingId(r.request_id);
        setError('');
        try {
            const res: any = await moduleApi.sendExtrasRequest(r.partner_id, r.request_id);
            if (res?.error) { setError(res.error); return; }
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi de la demande.");
        } finally {
            setSendingId(null);
        }
    };

    const handleConfirmManually = async (r: RequestEntry) => {
        setConfirmingId(r.request_id);
        setError('');
        try {
            await moduleApi.confirmExtrasRequestManually(r.partner_id, r.request_id);
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la confirmation.');
        } finally {
            setConfirmingId(null);
        }
    };

    // sansConfirmation : la question est posée UNE fois au niveau du groupe, pas une fois par poste.
    const handleCancel = async (r: RequestEntry, sansConfirmation = false) => {
        if (!sansConfirmation && !confirm(`Annuler la demande "${r.type_label}" ?`)) return;
        setCancelingId(r.request_id);
        setError('');
        try {
            const res: any = await moduleApi.cancelExtrasRequest(r.partner_id, r.request_id);
            if (res?.error) { setError(res.error); return; }
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'annulation.");
        } finally {
            setCancelingId(null);
        }
    };

    const [downloadingRecapId, setDownloadingRecapId] = React.useState<string | null>(null);
    // Événements pour lesquels le rattachement automatique du récap a déjà été tenté cette session — évite
    // de re-générer/re-uploader à chaque re-render (le check "déjà présent dans Documents" côté serveur
    // suffit à éviter les doublons entre sessions, ceci évite juste des appels réseau redondants ici).
    const autoAttachedEventIds = React.useRef<Set<string>>(new Set());

    // Récap PDF des extras VALIDÉS par le(s) prestataire(s) pour une privatisation — mêmes fiches
    // Prestataire(s)/Client + date & heure que le récap de commande fournisseur, jamais les suggestions de
    // personnes (de simples souhaits, pas des engagements). Demande explicite d'Emeni (2026-08-22) : "ça doit
    // ressembler au récap commande [...] avec infos partenaire, infos client, date privat heure, et les
    // types d'extras avec leurs heures bookées". `download=false` permet un rattachement silencieux en
    // arrière-plan (voir l'effet plus bas) sans forcer un téléchargement local intempestif.
    const buildAndAttachRecap = async (eventId: string | null, confirmed: RequestEntry[], download: boolean): Promise<boolean> => {
        if (confirmed.length === 0) return false;
        const distinctPartnerIds = Array.from(new Set(confirmed.map((r) => r.partner_id)));
        const [event, ...partners] = await Promise.all([
            eventId ? moduleApi.getEvenementielEvent(eventId).catch(() => null) : Promise.resolve(null),
            ...distinctPartnerIds.map((id) => moduleApi.getExtrasPartner(id).catch(() => null)),
        ]);
        const eventLabel = confirmed[0].event_label || 'Privatisation';
        // Le rattachement SILENCIEUX ne régénère jamais un doublon ; un clic manuel sur le bouton, lui,
        // régénère/re-télécharge toujours, même si un récap est déjà présent (l'admin peut vouloir une
        // version à jour après une nouvelle confirmation).
        if (!download && eventId) {
            const expectedName = `Extras_confirmes_${eventLabel.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
            const already = Array.isArray((event as any)?.documents) && (event as any).documents.some((d: any) => d.file_name === expectedName);
            if (already) return false;
        }
        // UNE PRIVATISATION SUPPRIMÉE LAISSE DES DEMANDES D'EXTRAS ORPHELINES : `event` vaut alors null.
        // Continuer produisait un POST .../documents voué à un 404. L'erreur était avalée côté navigateur
        // (personne ne voyait rien), mais le SERVEUR la comptait : au bout de 5 requêtes vers des routes
        // inexistantes en 2 minutes, le bannissement automatique coupe l'accès à TOUTE l'application, pas
        // seulement à ce module. Deux lignes orphelines par ouverture suffisaient donc à bannir un client
        // en trois consultations. Constaté en production le 27/08, sur des comptes réels.
        //
        // Un clic MANUEL continue de produire le PDF — l'utilisateur l'a demandé, il doit l'obtenir — mais
        // sans tenter de le rattacher à une privatisation qui n'existe plus.
        // ATTENTION : GET /evenementiel/:id repond 200 avec une COQUILLE {id, note_text, staff,
        // assignments, spaces} meme pour un evenement supprime — il la fabrique a partir de l'identifiant.
        // Tester la nullite de `event` ne suffit donc pas, il faut un champ que seule une vraie ligne porte.
        const privatisationSupprimee = !!eventId && !(event as any)?.start_time;
        if (privatisationSupprimee && !download) return false;
        const result = await generateExtrasConfirmedPdf({
            eventLabel,
            eventDate: confirmed[0].request_date || undefined,
            eventStartTime: (event as any)?.start_time ? String((event as any).start_time).slice(11, 16) : undefined,
            eventEndTime: (event as any)?.end_time ? String((event as any).end_time).slice(11, 16) : undefined,
            numPeople: (event as any)?.num_people || null,
            clientPhone: (event as any)?.phone || undefined,
            clientEmail: (event as any)?.email || undefined,
            partners: distinctPartnerIds.map((id, i) => {
                const p: any = partners[i];
                const name = confirmed.find((r) => r.partner_id === id)?.partner_name || p?.name || '';
                return { name, phone: p?.phone || undefined, email: p?.email || undefined };
            }),
            items: confirmed.map((r) => ({
                type_label: r.type_label, partner_name: r.partner_name, quantity: r.quantity,
                start_time: r.start_time, end_time: r.end_time, confirmed_comment: r.confirmed_comment,
            })),
            issuerName: user?.company_name || undefined,
            logoUrl: user?.logo_url ? resolveLogoUrl(user.logo_url) : null,
            brandColor: user?.theme_color || undefined,
            download,
        });
        // Rattaché automatiquement aux "Documents" de la privatisation concernée — demande explicite d'Emeni
        // (2026-08-22). Jamais si la privatisation a disparu : il n'y a plus rien où le ranger.
        if (result && eventId && !privatisationSupprimee) {
            await moduleApi.addEvenementielDocument(eventId, { file_name: result.fileName, mime_type: result.mimeType, base64: result.base64 }).catch(() => {});
            return true;
        }
        return !!result;
    };

    // Rattachement AUTOMATIQUE, sans action de l'admin, dès qu'un groupe (partenaire + événement) est
    // entièrement visible ici avec au moins une ligne confirmée — couvre le cas où le prestataire confirme
    // lui-même via son lien reçu par email (une session totalement différente, où le PDF ne peut pas être
    // généré à la volée puisqu'il l'est côté navigateur) : la prochaine fois qu'un admin ouvre ce module, le
    // document est silencieusement généré et rattaché s'il ne l'est pas déjà. Demande explicite d'Emeni
    // (2026-08-22) : "quand les extras sont confirmés par le prestataire, le document doit s'ajouter tout seul".
    React.useEffect(() => {
        if (confirmeesParPrestataire.length === 0) return;
        const byEvent = new Map<string, RequestEntry[]>();
        confirmeesParPrestataire.forEach((r) => {
            if (!r.event_id || autoAttachedEventIds.current.has(r.event_id)) return;
            if (!byEvent.has(r.event_id)) byEvent.set(r.event_id, []);
            byEvent.get(r.event_id)!.push(r);
        });
        byEvent.forEach((rows, eventId) => {
            autoAttachedEventIds.current.add(eventId);
            buildAndAttachRecap(eventId, rows, false).catch(() => {});
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmeesParPrestataire]);

    const handleDownloadConfirmedRecap = async (rows: RequestEntry[]) => {
        const confirmed = rows.filter((r) => deriveStatus(r) === 'confirmee');
        if (confirmed.length === 0) return;
        const eventId = confirmed[0].event_id;
        setDownloadingRecapId(eventId || confirmed[0].request_id);
        try {
            await buildAndAttachRecap(eventId, confirmed, true);
        } finally {
            setDownloadingRecapId(null);
        }
    };

    const allRows = React.useMemo(() => [...aDemander, ...demandees, ...confirmeesParPrestataire], [aDemander, demandees, confirmeesParPrestataire]);

    // ── Dossiers par privatisation ────────────────────────────────────────────────────────────────
    // Le suivi presentait une ligne par (privatisation, partenaire), avec TOUS les postes empiles dans la
    // colonne "Type", separes par des virgules — "on ne comprend pas trop" (Emeni, 2026-08-28). Et son
    // statut etait celui du poste le moins avance, si bien qu'un dossier a moitie confirme s'annoncait
    // "a demander". Desormais : un dossier par privatisation, une LIGNE PAR POSTE, et trois compteurs
    // separes (requis / interne / prestataire) puisque le process passe d'abord par les salaries de la
    // maison. L'etat de chaque poste vient de etatDuPoste — la meme fonction que la fiche de privatisation.
    const dossiers = React.useMemo(() => {
        const toutes = [...allRows, ...besoinsSansDemande];
        const parEvenement = new Map<string, RequestEntry[]>();
        toutes.forEach((r) => {
            const cle = r.event_id || `seule::${r.request_id}`;
            if (!parEvenement.has(cle)) parEvenement.set(cle, []);
            parEvenement.get(cle)!.push(r);
        });
        return Array.from(parEvenement.entries()).map(([cle, lignes]) => {
            const parType = new Map<string, RequestEntry[]>();
            lignes.forEach((r) => {
                const t = String(r.extra_type_id || r.type_label || '');
                if (!parType.has(t)) parType.set(t, []);
                parType.get(t)!.push(r);
            });
            const postes: PosteSuivi[] = Array.from(parType.entries()).map(([typeId, l]) => {
                // Les totaux sont deja agreges par (privatisation, poste) cote serveur : inutile de les
                // resommer ligne a ligne, ce serait les compter plusieurs fois.
                const ref = l[0];
                return {
                    typeId,
                    label: ref.type_label || '',
                    requis: Number(ref.need_total) || 0,
                    interne: Number(ref.internal_total) || 0,
                    commande: Number(ref.requested_total) || 0,
                    envoye: Number(ref.sent_total) || 0,
                    confirme: Number(ref.confirmed_total) || 0,
                    tenableEnInterne: !!ref.internal_possible,
                    slots: l.filter((x) => x.start_time && x.quantity > 0)
                        .map((x) => ({ start: String(x.start_time).slice(0, 5), end: String(x.end_time || '').slice(0, 5), quantity: x.quantity })),
                };
            });
            const etat = etatLeMoinsAvance(postes.map((p) => etatDuPoste(p).etat));
            const avecDemande = lignes.filter((r) => r.request_id);
            return {
                cle,
                label: lignes[0].event_label || '',
                date: lignes[0].request_date || '',
                postes,
                etat,
                lignes: avecDemande,
                // Un email couvre un partenaire : les boutons se posent donc par partenaire, pas par dossier.
                parPartenaire: Array.from(avecDemande.reduce((m, r) => {
                    const k = String(r.partner_id || '');
                    if (!m.has(k)) m.set(k, []);
                    m.get(k)!.push(r);
                    return m;
                }, new Map<string, RequestEntry[]>()).values()),
            };
        }).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    }, [allRows, besoinsSansDemande]);

    const dossiersFiltres = React.useMemo(
        () => etatFilter === 'all' ? dossiers : dossiers.filter((d) => d.postes.some((p) => etatDuPoste(p).etat === etatFilter)),
        [dossiers, etatFilter]);

    const filteredRows = React.useMemo(() => statusFilter === 'all' ? allRows : allRows.filter((r) => deriveStatus(r) === statusFilter), [allRows, statusFilter]);

    // UNE ligne par privatisation ET par partenaire : le suivi en affichait une par type de poste,
    // soit deux fois le nom du partenaire, deux fois la date et deux boutons pour un seul et meme
    // email. Demande d'Emeni (2026-08-25).
    const groupedRows = React.useMemo(() => groupByPartnerAndEvent(filteredRows), [filteredRows]);

    // Les pastilles comptent des POSTES, plus des lignes de demande : c'est l'unite que l'ecran affiche
    // desormais. Les intitules disent qui doit agir — critere retenu avec Emeni (2026-08-28).
    const compterEtat = (cible: EtatPoste) =>
        dossiers.reduce((n, dos) => n + dos.postes.filter((p) => etatDuPoste(p).etat === cible).length, 0);
    const chipOptions = [
        { value: 'a_booker_interne', label: LIBELLE_ETAT.a_booker_interne, count: compterEtat('a_booker_interne') },
        { value: 'a_commander', label: LIBELLE_ETAT.a_commander, count: compterEtat('a_commander') },
        { value: 'reponse_attendue', label: LIBELLE_ETAT.reponse_attendue, count: compterEtat('reponse_attendue') },
        { value: 'complet', label: LIBELLE_ETAT.complet, count: compterEtat('complet') },
    ];

    // Réutilisée à la fois pour les actions en ligne du tableau et pour le pied de la modale de détail
    // (Emeni : "quand je clique sur une commande extras, il m'affiche toutes les infos + les boutons
    // disponibles") — `afterAction` ferme la modale une fois une action déclenchée depuis celle-ci.
    const handleCancelGroup = async (lot: RequestEntry[]) => {
        if (lot.length > 1) {
            const libelles = lot.map((x) => `${x.type_label} x${x.quantity}`).join(', ');
            if (!confirm(`Annuler les ${lot.length} demandes de cette privatisation ?

${libelles}`)) return;
        }
        for (const item of lot) await handleCancel(item, true);
    };
    const handleConfirmGroup = async (lot: RequestEntry[]) => {
        for (const item of lot) await handleConfirmManually(item);
    };

    // `group` : toutes les lignes de la ligne groupée. Annuler ou confirmer doit les traiter TOUTES,
    // sinon un clic n'en retirerait qu'une et la ligne resterait à l'écran avec le reste.
    const renderRowActions = (r: RequestEntry, afterAction?: () => void, group?: RequestEntry[]) => {
        const lot = group && group.length ? group : [r];
        const status = deriveStatus(r);
        // Le serveur enverra un SEUL email couvrant tout le lot : le bouton doit le dire, sinon deux
        // lignes du meme partenaire affichent deux boutons identiques et on croit a un doublon.
        const batchSize = sameEmailBatch(allRows, r).length;
        return (
            <>
                {status === 'a_demander' && (
                    <>
                        <Button size="sm" onClick={() => { handleSend(r); afterAction?.(); }} disabled={sendingId === r.request_id || cancelingId === r.request_id}>
                            {sendingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            {batchActionLabel('Envoyer', batchSize)}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleCancelGroup(lot); afterAction?.(); }} disabled={sendingId === r.request_id || cancelingId === r.request_id}>
                            {cancelingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                        </Button>
                    </>
                )}
                {status === 'demandee' && (
                    <>
                        <Button size="sm" variant="secondary" onClick={() => { handleSend(r); afterAction?.(); }} disabled={sendingId === r.request_id || confirmingId === r.request_id || cancelingId === r.request_id}>
                            {sendingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            {batchActionLabel('Relancer', batchSize)}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleConfirmGroup(lot); afterAction?.(); }} disabled={confirmingId === r.request_id || cancelingId === r.request_id}>
                            {confirmingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <BadgeCheck size={13} />}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleCancelGroup(lot); afterAction?.(); }} disabled={confirmingId === r.request_id || cancelingId === r.request_id}>
                            {cancelingId === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                        </Button>
                    </>
                )}
                <Button size="sm" variant="secondary" onClick={() => { setEmailHistoryFor(r); afterAction?.(); }} title="Historique des mails">
                    <Mail size={13} />
                </Button>
            </>
        );
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2"><HardHat size={18} /> Extras</h1>
                <p className="text-sm text-content-muted mt-1">Toutes les demandes d'extras, tous partenaires confondus.</p>
            </div>

            <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un type, un partenaire, une privatisation..." className="pl-8" />
                </div>
                <Button type="submit" variant="secondary" size="sm">Rechercher</Button>
            </form>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <StatusFilterChips options={chipOptions} value={etatFilter} onChange={(v) => setEtatFilter(v as any)} />

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : dossiersFiltres.length === 0 ? (
                <p className="text-sm text-content-muted py-12 text-center">Aucune demande pour l'instant.</p>
            ) : (
                <div className="space-y-3">
                    {dossiersFiltres.map((dossier) => (
                        <div key={dossier.cle} className="rounded-2xl border border-border bg-surface p-4 space-y-3">
                            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="font-bold text-content text-sm">{dossier.label || 'Sans privatisation'}</p>
                                    {dossier.date && (
                                        <p className="text-xs text-content-muted flex items-center gap-1 mt-0.5">
                                            <CalendarDays size={11} /> {fmtDate(dossier.date)}
                                        </p>
                                    )}
                                </div>
                                <span className="text-[11px] font-bold text-content-muted">{LIBELLE_ETAT[dossier.etat]}</span>
                            </div>

                            <SuiviExtrasParPoste postes={dossier.postes} />

                            {/* Un email couvre TOUT ce qui est destine au meme partenaire : les boutons se
                                posent donc une fois par partenaire, jamais par poste, sinon deux lignes du
                                meme prestataire afficheraient deux boutons pour un seul et meme envoi. */}
                            {dossier.parPartenaire.map((lot) => (
                                <div key={String(lot[0].partner_id || '')} className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
                                    <PartnerBadge name={lot[0].partner_name} id={lot[0].partner_id} />
                                    {renderRowActions(lot[0], undefined, lot)}
                                </div>
                            ))}

                            {dossier.lignes.some((r) => deriveStatus(r) === 'confirmee') && (
                                <div className="pt-2 border-t border-border">
                                    <Button size="sm" variant="secondary" onClick={() => handleDownloadConfirmedRecap(dossier.lignes)}
                                        disabled={downloadingRecapId === (dossier.lignes[0]?.event_id || dossier.lignes[0]?.request_id)}>
                                        {downloadingRecapId === (dossier.lignes[0]?.event_id || dossier.lignes[0]?.request_id) ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Récap extras confirmés
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {selectedEntry && (
                <RequestDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)}
                    actions={renderRowActions(selectedEntry, () => setSelectedEntry(null))} />
            )}
            {emailHistoryFor && (
                <EmailHistoryModal
                    title={`Historique des mails — ${emailHistoryFor.type_label}`}
                    fetchHistory={() => moduleApi.getExtrasRequestEmailHistory(emailHistoryFor.partner_id, emailHistoryFor.request_id).then((res: any) => Array.isArray(res?.emails) ? res.emails : [])}
                    onClose={() => setEmailHistoryFor(null)}
                />
            )}
        </div>
    );
};
